import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, TrendingDown, MinusCircle, X } from "lucide-react";
import { API } from "@/lib/api";
import { todayUKDate } from "@/lib/device";

const DISMISS_KEY_PREFIX = "dr_recap_seen_";

const STYLES = {
  won: {
    icon: Trophy,
    label: "WON",
    border: "border-[#4ADE80]/40",
    bg: "bg-[#4ADE80]/8",
    accent: "text-[#4ADE80]",
    tint: "from-[#4ADE80]/10",
  },
  lost: {
    icon: TrendingDown,
    label: "LOST",
    border: "border-[#F87171]/30",
    bg: "bg-[#F87171]/5",
    accent: "text-[#F87171]",
    tint: "from-[#F87171]/8",
  },
  void: {
    icon: MinusCircle,
    label: "VOID",
    border: "border-zinc-800",
    bg: "bg-zinc-900",
    accent: "text-zinc-400",
    tint: "from-zinc-800/10",
  },
};

export default function YesterdayRecap() {
  const [tip, setTip] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const key = DISMISS_KEY_PREFIX + todayUKDate();
    if (localStorage.getItem(key) === "1") return;
    let mounted = true;
    fetch(`${API}/tips/yesterday`)
      .then((r) => r.json())
      .then((j) => {
        // Only surface yesterday's result when it was a winner —
        // avoids leading with a loss which kills conversion.
        if (mounted && j.tip && j.tip.result === "won") {
          setTip(j.tip);
          setVisible(true);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY_PREFIX + todayUKDate(), "1");
    setVisible(false);
  };

  if (!visible || !tip) return null;
  const s = STYLES[tip.result] || STYLES.void;
  const pts = tip.return_pts;
  const ptsLabel =
    pts === null || pts === undefined
      ? null
      : `${pts > 0 ? "+" : ""}${pts}pts`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, y: -8, height: 0 }}
          transition={{ duration: 0.4 }}
          className="overflow-hidden pt-6"
          data-testid="yesterday-recap"
        >
          <div
            className={`relative flex items-center gap-3 rounded-2xl border ${s.border} ${s.bg} bg-gradient-to-r ${s.tint} via-transparent to-transparent p-4`}
          >
            <div className={`flex h-9 w-9 flex-none items-center justify-center rounded-full bg-black/40 ${s.accent}`}>
              <s.icon size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-[0.28em] text-zinc-500">
                Yesterday
              </div>
              <div className="mt-0.5 flex items-baseline gap-2 truncate">
                <span className={`font-mono-data text-[11px] font-bold uppercase tracking-widest ${s.accent}`}>
                  {s.label}
                </span>
                <span className="font-display truncate text-base text-white">
                  {tip.selection}
                </span>
                {tip.odds && (
                  <span className="font-mono-data text-xs text-zinc-500">
                    · {tip.odds}
                  </span>
                )}
                {ptsLabel && tip.result === "won" && (
                  <span className={`font-mono-data text-xs font-bold ${s.accent}`}>
                    {ptsLabel}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={dismiss}
              data-testid="yesterday-recap-dismiss"
              aria-label="Dismiss"
              className="rounded-full border border-zinc-800 p-1.5 text-zinc-500 hover:text-white"
            >
              <X size={12} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
